import { useEffect, useRef, useState, type MouseEvent } from 'react';

/**
 * The portal's jump menu (v2.1 design §1).
 *
 * TIER-BLIND BY CONSTRUCTION. This component invents nothing: it renders exactly the entries
 * it is handed, and the page builds that list from the SAME conditions that render the
 * sections. A buyer link therefore never shows a Proceeds chip, and a deal with no geometry
 * shows no Map chip - with zero code changes here. Adding a hardcoded section list to this
 * file would re-introduce the leak the tier filter exists to prevent.
 *
 * The scroll-spy is split deliberately: `activeSectionFrom` is a PURE chooser and is unit
 * tested; the IntersectionObserver that feeds it is browser-verified. jsdom ships no
 * IntersectionObserver at all, so the wiring is guarded on its existence rather than mocked -
 * the nav still renders and still jumps under test, it simply has no active chip.
 */

export interface PortalNavEntry {
  key: string;
  label: string;
}

/** An entry listed under a section in the phone menu: the DOM id it jumps to, the label and count its heading prints. */
export interface PortalNavSub {
  id: string;
  label: string;
  count: number;
}

/**
 * The phone menu (TP7, his ask 2026-10-13: "make the sub menus more intricate in detail so it makes sense for
 * mobile"). On a phone the row showed two and a half sections and hid the rest off to the right with nothing to say
 * so. A page that passes this gets one "Sections" button there instead, opening EVERY section, each with the count
 * its own heading prints (`counts`, by section key) and the groups that section holds beneath it (`subs`).
 * ⛔ Only a count the page itself prints on that heading - a second number for the same thing reads as a mistake.
 * Which of the two a screen draws is the stylesheet's call; a page that passes nothing keeps the row, unchanged.
 */
export interface PortalPhoneMenu {
  counts?: Record<string, number>;
  subs?: Record<string, PortalNavSub[]>;
}

/** The DOM id a section carries for its key. One place, so the nav and the page agree. */
export const sectionElementId = (key: string): string => `section-${key}`;

/**
 * Which chip is current, given the sections currently on screen and the page's section order.
 *
 * First visible in DOCUMENT order wins. The observer batches its records in whatever order it
 * pleases and re-fires as the reader scrolls, so "the one that became visible most recently"
 * is not a stable answer; document order is the ordering a reader actually perceives.
 */
export const activeSectionFrom = (visible: string[], order: string[]): string | null => {
  for (const key of order) {
    if (visible.includes(key)) return key;
  }
  return null;
};

/**
 * "Parcels (3)" - the count in its own span, so the label stays the label. The space sits OUTSIDE the span: a
 * screen reader's name for the link drops a span's leading space, and read "Parcels(3)" (measured, 2026-10-13).
 */
const counted = (label: string, count: number | undefined) => (
  <>
    {label}
    {typeof count === 'number' && (
      <>
        {' '}
        <span className="portal-nav-count">({count})</span>
      </>
    )}
  </>
);

export default function PortalSectionNav({ entries, phoneMenu }: { entries: PortalNavEntry[]; phoneMenu?: PortalPhoneMenu }) {
  const [active, setActive] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // An open list closes on Escape and on a tap anywhere outside the menu - the two ways out a phone reader expects.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: Event) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  // The observer callback closes over this ONCE; a state Set would hand every callback the
  // set as it looked when the effect ran, so visibility would never accumulate.
  const visibleRef = useRef<Set<string>>(new Set());

  // A joined string, not the array: `entries` is rebuilt on every render of the page, and an
  // array dependency would tear down and re-create the observer on each one.
  const orderKey = entries.map((e) => e.key).join('|');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return; // jsdom, and very old browsers
    const order = orderKey ? orderKey.split('|') : [];
    const seen = visibleRef.current;
    seen.clear();

    const observer = new IntersectionObserver(
      (records) => {
        records.forEach((record) => {
          const key = (record.target as HTMLElement).id.replace(/^section-/, '');
          if (record.isIntersecting) seen.add(key);
          else seen.delete(key);
        });
        setActive(activeSectionFrom([...seen], order));
      },
      // The bottom inset keeps a section from claiming the chip while it is still a sliver at
      // the foot of the viewport; the top inset accounts for the sticky bar itself.
      { rootMargin: '-56px 0px -55% 0px', threshold: 0 }
    );

    order.forEach((key) => {
      const el = document.getElementById(sectionElementId(key));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [orderKey]);

  // No sections, no bar. A tier that strips every optional section must not leave an empty
  // rule floating under the masthead.
  if (!entries.length) return null;

  /*
    Click-to-scroll rather than `scroll-behavior: smooth`: that property has to live on the
    scrolling element (html), and this page's styles are scoped to `.portal` so nothing here
    can reach it. A handler is also the only version that is testable outside a browser.
  */
  const jumpTo = (event: MouseEvent<HTMLAnchorElement>, elementId: string, key: string) => {
    setOpen(false);
    const el = document.getElementById(elementId);
    if (!el) return; // fall through to the plain href rather than swallowing the click
    event.preventDefault();
    // A group is a <details>; a closed one has no layout to scroll to, so it opens first.
    if (el instanceof HTMLDetailsElement && !el.open) el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(key);
  };
  const jump = (event: MouseEvent<HTMLAnchorElement>, key: string) => jumpTo(event, sectionElementId(key), key);
  const current = entries.find((entry) => entry.key === active)?.label;

  return (
    <nav ref={navRef} className={phoneMenu ? 'portal-nav portal-nav-has-phone' : 'portal-nav'} aria-label="Sections">
      <div className="portal-nav-row">
        {entries.map((entry) => (
          <a
            key={entry.key}
            className={`portal-nav-link${entry.key === active ? ' portal-nav-active' : ''}`}
            href={`#${sectionElementId(entry.key)}`}
            aria-current={entry.key === active ? 'true' : undefined}
            onClick={(event) => jump(event, entry.key)}
          >
            {entry.label}
          </a>
        ))}
      </div>
      {phoneMenu && (
        <div className="portal-nav-phone">
          <button
            type="button"
            className="portal-nav-toggle"
            aria-expanded={open}
            aria-controls="portal-nav-panel"
            onClick={() => setOpen((o) => !o)}
          >
            Sections <span aria-hidden="true">{open ? '▴' : '▾'}</span>
          </button>
          {current && <span className="portal-nav-current">{current}</span>}
        </div>
      )}
      {phoneMenu && open && (
        <ol id="portal-nav-panel" className="portal-nav-panel">
          {entries.map((entry) => {
            const subs = phoneMenu.subs?.[entry.key] ?? [];
            return (
              <li key={entry.key}>
                <a
                  href={`#${sectionElementId(entry.key)}`}
                  aria-current={entry.key === active ? 'true' : undefined}
                  onClick={(event) => jump(event, entry.key)}
                >
                  {counted(entry.label, phoneMenu.counts?.[entry.key])}
                </a>
                {subs.length > 0 && (
                  <ol>
                    {subs.map((sub) => (
                      <li key={sub.id}>
                        <a href={`#${sub.id}`} onClick={(event) => jumpTo(event, sub.id, entry.key)}>
                          {counted(sub.label, sub.count)}
                        </a>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
}
